import { DI } from '@intigral/constant/config/DIContainer';
import { API_IDS } from '@intigral/constant/endpoint/ApiIdMapper';
import { IHttpAdapter } from '../../core/entities/iHttpAdapter';
import { ISubscriptionRepository } from '../../core/repositories/interfaces/iSubscriptionRepository';
import { container } from '../../DataDomain';
import { IUnsubscribeEntity, IUnsubscribeReqBody } from '../../entity';
import { IQrCodeLinkRequest, IQrCodeSuccessResponse } from '../../entity/interface/iQrCodes';
import {
    CampaignGenerateResponse,
    IBoltSubscriptionRequest,
    IBoltSubscriptionResponse,
    IBoltSubscriptionTwoStepsRequest,
    IBoltSubscriptionTwoStepsResponse,
    IBoltVerifyTwoStepsRequest,
    IBoltVerifyTwoStepsResponse,
    ICampaignVerifyResponse,
    ICheckUserResponse,
    ICheckUserRequest,
    IUserRegisterGeneratePin,
    IUserRegisterRequest,
    IUserRegisterVerifyPin,
    IValidateActivationLinkRequest,
    IValidateActivationLinkResponse,
    IDisneyGenerateOtpRequest,
    IDisneyGenerateOtpResponse,
    IDisneyValidateOtpRequest,
    IDisneyValidateOtpResponse,
    IDisneyRequestActivationLinkRequest,
    IDisneyRequestActivationLinkResponse,
    IPartnerActivationRedirectionRequest,
    IPartnerActivationRedirectionResponse,
} from '../../entity/interface/iSubscription';
import { ISubscriptionStatusResponse } from '../../entity/interface/iSubscriptionStatus';
import {
    ITpayPayload,
    ITpayRedirectSubscribeExistingUserEntity,
    ITpayRedirectSubscribeExistingUserReqBody,
    ITpayRedirectSubscribeNewUserEntity,
    ITpayRedirectSubscribeNewUserReqBody,
} from '../../entity/interface/iTPayRedirectSubscribe';
import { IApiPathManager } from '../config/ApiPathManager';
import { IQitafGenerateOtpPayload, IQitafVerifyOtpPayload } from '../../entity/interface/iEntitlements';

class SubscriptionRepository implements ISubscriptionRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    private get appKey(): string {
        return process.env['NX_PUBLIC_APP_KEY'] || 'APP_KEY';
    }

    private get productKey(): string {
        return process.env['NX_PUBLIC_PRODUCT_KEY'] || 'stc-tv';
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async tpayRedirectSubscribeNew(
        username: string,
        msisdn: string,
        tpay: ITpayPayload,
        reqBody: ITpayRedirectSubscribeNewUserReqBody
    ): Promise<ITpayRedirectSubscribeNewUserEntity> {
        return this.http
            .post<ITpayRedirectSubscribeNewUserEntity>(
                this.apiPathManager.getPath(API_IDS.USER_TPAY_REDIRECT_SUBSCRIBE_NEW_USER),
                {
                    body: reqBody,
                    params: {
                        appKey: this.appKey,
                        username,
                        msisdn,
                    },
                    headers: {
                        tpaySessionToken: tpay.tpaySessionToken,
                        tpaySignature: tpay.tpaySignature,
                        headerEnrichmentReferenceCode: tpay.headerEnrichmentReferenceCode,
                    },
                }
            )
            .then((result) => result.data);
    }

    async tpayRedirectSubscribeExisting(
        username: string,
        msisdn: string,
        tpay: ITpayPayload,
        reqBody: ITpayRedirectSubscribeExistingUserReqBody
    ): Promise<ITpayRedirectSubscribeExistingUserEntity> {
        return this.http
            .post<ITpayRedirectSubscribeExistingUserEntity>(
                this.apiPathManager.getPath(API_IDS.USER_TPAY_REDIRECT_SUBSCRIBE_EXISTING_USER),
                {
                    body: reqBody,
                    params: {
                        appKey: this.appKey,
                        username,
                        msisdn,
                    },
                    headers: {
                        tpaySessionToken: tpay.tpaySessionToken,
                        tpaySignature: tpay.tpaySignature,
                        headerEnrichmentReferenceCode: tpay.headerEnrichmentReferenceCode,
                    },
                }
            )
            .then((result) => result.data);
    }

    async unsubscribe(username: string, token: string, reqBody: IUnsubscribeReqBody): Promise<IUnsubscribeEntity> {
        return this.http
            .put<IUnsubscribeEntity>(this.apiPathManager.getPath(API_IDS.USER_UNSUBSCRIBE), {
                withCredentials: true,
                body: reqBody,
                params: {
                    username,
                    appKey: this.appKey,
                },
                headers: {
                    Authorization: token,
                },
            })
            .then((result) => result.data);
    }

    async getSubscriptionStatus(
        apiKey: string,
        username: string,
        token: string,
        identityToken: string
    ): Promise<ISubscriptionStatusResponse> {
        const response = await this.http.get<ISubscriptionStatusResponse>(
            this.apiPathManager.getPath(API_IDS.USER_SUBSCRIPTION_STATUS_NEW),
            {
                headers: {
                    Authorization: token,
                    identityToken: identityToken,
                },
                params: {
                    apikey: apiKey,
                    userName: username,
                    productKey: this.productKey,
                },
            }
        );
        return response.data;
    }

    async boltUserSubscription(
        payload: IBoltSubscriptionRequest,
        apiToken: { [key: string]: string }
    ): Promise<IBoltSubscriptionResponse> {
        const response = await this.http.post<IBoltSubscriptionResponse>(
            this.apiPathManager.getPath(API_IDS.SLP_BOLT_SUBSCRIPTION),
            {
                withCredentials: true,
                body: {
                    ...payload,
                    ...apiToken,
                },
            }
        );

        return response.data;
    }

    async boltUserSubscriptionTwoSteps(
        payload: IBoltSubscriptionTwoStepsRequest,
        apiToken: { [key: string]: string }
    ): Promise<IBoltSubscriptionTwoStepsResponse> {
        const response = await this.http.post<IBoltSubscriptionTwoStepsResponse>(
            this.apiPathManager.getPath(API_IDS.SLP_BOLT_SUBSCRIPTION_TWO_STEPS),
            {
                withCredentials: true,
                body: {
                    ...payload,
                    ...apiToken,
                },
            }
        );

        return response.data;
    }

    async boltUserVerificationTwoSteps(
        payload: IBoltVerifyTwoStepsRequest,
        apiToken: { [key: string]: string }
    ): Promise<IBoltVerifyTwoStepsResponse> {
        const response = await this.http.post<IBoltVerifyTwoStepsResponse>(
            this.apiPathManager.getPath(API_IDS.SLP_BOLT_VERIFICATION_TWO_STEPS),
            {
                withCredentials: true,
                body: {
                    ...payload,
                    ...apiToken,
                },
            }
        );

        return response.data;
    }

    async qitafGenerateOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: IQitafGenerateOtpPayload,
        apiToken: { [key: string]: string },
        identityToken?: string | undefined,
        token?: string | undefined
    ): Promise<any> {
        const response = await this.http.post<any>(this.apiPathManager.getPath(API_IDS.SLP_QITAF_GENERATE_OTP), {
            withCredentials: true,
            body: {
                subType,
                language,
                token,
                identityToken,
                ...payload,
                ...apiToken,
            },
        });
        return response.data;
    }

    async qitafVerifyOtp(
        subType: 'NEW' | 'EXISTING',
        language: string,
        payload: IQitafVerifyOtpPayload,
        apiToken: { [key: string]: string },
        identityToken?: string | undefined,
        token?: string | undefined
    ): Promise<any> {
        const response = await this.http.post<any>(this.apiPathManager.getPath(API_IDS.SLP_QITAF_VERIFY_OTP), {
            withCredentials: true,
            body: {
                subType,
                language,
                token,
                identityToken,
                ...payload,
                ...apiToken,
            },
        });
        return response.data;
    }

    async userRegister(
        language: string,
        payload: IUserRegisterRequest,
        apiToken: { [key: string]: string }
    ): Promise<any> {
        const response = await this.http.post<any>(this.apiPathManager.getPath(API_IDS.SLP_USER_REGISTRATION), {
            withCredentials: true,
            body: {
                language,
                ...payload,
                ...apiToken,
            },
        });
        return response.data;
    }

    async userRegisterGeneratePin(
        language: string,
        payload: IUserRegisterGeneratePin,
        apiToken: { [key: string]: string }
    ): Promise<any> {
        const response = await this.http.post<any>(
            this.apiPathManager.getPath(API_IDS.SLP_USER_REGISTRATION_GENERATE_PIN),
            {
                withCredentials: true,
                body: {
                    language,
                    ...payload,
                    ...apiToken,
                },
            }
        );
        return response.data;
    }

    async userRegisterVerifyPin(
        language: string,
        payload: IUserRegisterVerifyPin,
        apiToken: { [key: string]: string }
    ): Promise<any> {
        const response = await this.http.post<any>(
            this.apiPathManager.getPath(API_IDS.SLP_USER_REGISTRATION_VERIFY_PIN),
            {
                withCredentials: true,
                body: {
                    language,
                    ...payload,
                    ...apiToken,
                },
            }
        );
        return response.data;
    }

    // $rules = [
    //     'username' => 'trim|required',
    //     FIELD_AUTH_TOKEN => 'required|string',
    //     'operator' => 'trim|required',
    //     'language' => 'permit_empty',
    //     'subscriptionGuids.*' => 'permit_empty',
    //     'tpaySessionToken' => 'permit_empty',
    //     'discountCode' => 'permit_empty',
    //     'qitafRefId' => 'permit_empty',
    //     'tierGUID' => 'permit_empty',
    //     'newMsisdn' => 'permit_empty',
    //     'countryCode' => 'permit_empty',

    // ];

    async RBOResub(payload: object, language: string, apiToken: { [key: string]: string }): Promise<any> {
        const response = await this.http.post<any>(API_IDS.RBO_RESUB, {
            withCredentials: true,
            body: {
                language,
                ...payload,
                ...apiToken,
            },
        });
        return response.data;
    }

    async RBOResubVerify(payload: object, language: string, apiToken: { [key: string]: string }): Promise<any> {
        const response = await this.http.post<any>(API_IDS.RBO_RESUB_VERIFY, {
            withCredentials: true,
            body: {
                language,
                ...payload,
                ...apiToken,
            },
        });
        return response.data;
    }

    async qrCodeLink(
        payload: IQrCodeLinkRequest,
        apiToken: { [key: string]: string }
    ): Promise<IQrCodeSuccessResponse> {
        const response = await this.http.post<IQrCodeSuccessResponse>(
            this.apiPathManager.getPath(API_IDS.QR_CODE_LINK),
            {
                withCredentials: true,
                body: {
                    ...payload,
                    recaptchaToken: payload.recaptchaToken,
                    ...apiToken,
                },
            }
        );
        return response.data;
    }

    async campaignGenerate(
        username: string,
        language: string,
        recaptchaToken?: string,
        apiToken?: { [key: string]: string }
    ): Promise<CampaignGenerateResponse> {
        const response = await this.http.post<CampaignGenerateResponse>(
            this.apiPathManager.getPath(API_IDS.CAMPAIGN_GENERATE),
            {
                withCredentials: true,
                body: {
                    username,
                    lang: language,
                    ...apiToken,
                },
            }
        );

        return response.data;
    }
    async campaignVerify(
        username: string,
        language: string,
        otp: string,
        apiToken?: { [key: string]: string }
    ): Promise<ICampaignVerifyResponse> {
        const response = await this.http.post<ICampaignVerifyResponse>(
            this.apiPathManager.getPath(API_IDS.CAMPAIGN_VERIFY),
            {
                withCredentials: true,
                body: {
                    username,
                    lang: language,
                    otp,
                    ...apiToken,
                },
            }
        );

        return response.data;
    }

    async checkUser(payload: ICheckUserRequest, apiToken: { [key: string]: string }): Promise<ICheckUserResponse> {
        const path = this.apiPathManager.getPath(API_IDS.CHECK_USER);
        const response = await this.http.post<ICheckUserResponse>(path, {
            withCredentials: true,
            body: {
                username: payload.username,
                lang: payload.lang,
                recaptchaToken: payload.recaptchaToken,
                ...apiToken,
            },
        });
        return response.data;
    }

    async validateActivationLink(
        params: IValidateActivationLinkRequest,
        apiToken?: { [key: string]: string }
    ): Promise<IValidateActivationLinkResponse> {
        const apiPath = this.apiPathManager.getPath(API_IDS.SUBSCRIPTION_VALIDATE_ACTIVATION_LINK);
        const response = await this.http.post<IValidateActivationLinkResponse>(apiPath, {
            withCredentials: true,
            body: {
                activationCode: params.activationCode,
                ...apiToken,
            },
        });
        return response.data;
    }

    async disneyGenerateOtp(
        params: IDisneyGenerateOtpRequest,
        apiToken?: { [key: string]: string }
    ): Promise<IDisneyGenerateOtpResponse> {
        const apiPath = this.apiPathManager.getPath(API_IDS.SUBSCRIPTION_DISNEY_GENERATE_OTP);

        const response = await this.http.post<IDisneyGenerateOtpResponse>(apiPath, {
            withCredentials: true,
            body: {
                msisdn: params.msisdn,
                token: params.token,
                language: params.acceptLanguage,
                ...apiToken,
            },
        });
        return response.data;
    }

    async disneyValidateOtp(
        params: IDisneyValidateOtpRequest,
        apiToken?: { [key: string]: string }
    ): Promise<IDisneyValidateOtpResponse> {
        const response = await this.http.post<IDisneyValidateOtpResponse>(
            this.apiPathManager.getPath(API_IDS.SUBSCRIPTION_DISNEY_VALIDATE_OTP),
            {
                withCredentials: true,
                body: {
                    token: params.token,
                    msisdn: params.msisdn,
                    pinCode: params.pinCode,
                    ...apiToken,
                },
            }
        );
        return response.data;
    }

    async disneyRequestActivationLink(
        params: IDisneyRequestActivationLinkRequest,
        apiToken: { [key: string]: string }
    ): Promise<IDisneyRequestActivationLinkResponse> {
        const apiPath = this.apiPathManager.getPath(API_IDS.SUBSCRIPTION_DISNEY_REQUEST_ACTIVATION_LINK);
        const response = await this.http.post<IDisneyRequestActivationLinkResponse>(apiPath, {
            withCredentials: true,
            body: {
                lang: params.lang,
                channel: params.channel,
                partnerName: params.partnerName,
                accountId: params.accountId,
                identityToken: params.identityToken,
                ...apiToken,
            },
        });
        return response.data;
    }

    async partnerActivationRedirection(
        payload: IPartnerActivationRedirectionRequest,
        apiToken: { [key: string]: string }
    ): Promise<IPartnerActivationRedirectionResponse> {
        const apiPath = this.apiPathManager.getPath(API_IDS.SUBSCRIPTION_PARTNER_ACTIVATION_REDIRECTION);
        const response = await this.http.post<IPartnerActivationRedirectionResponse>(apiPath, {
            withCredentials: true,
            body: {
                ...payload,
                ...apiToken,
            },
        });
        return response.data;
    }
}

export default SubscriptionRepository;
